import CryptoJS from 'crypto-js';
import logger from './logger';

// Hours since a login timestamp (YYYYMMDDHH, local time)
const hoursSince = (timestamp) => {
  const t = new Date(
    parseInt(timestamp.slice(0, 4)),
    parseInt(timestamp.slice(4, 6)) - 1,
    parseInt(timestamp.slice(6, 8)),
    parseInt(timestamp.slice(8, 10))
  );
  return (Date.now() - t) / (1000 * 60 * 60);
};

const LOGIN_STORE_KEY = 'sharpstackLogin';

// Drop the saved login copy (expired, or about to force a fresh login)
export const clearSavedLogin = () => {
  try { localStorage.removeItem(LOGIN_STORE_KEY); } catch { /* storage blocked */ }
};

// Set when this load found a saved login past 24h, so the guest banner can
// say the login expired rather than that it never arrived.
let savedLoginExpired = false;

// The saved login copy while it's under 24h old (the age the server accepts);
// a stale or unreadable copy is removed.
const readSavedLogin = () => {
  try {
    const saved = new URLSearchParams(localStorage.getItem(LOGIN_STORE_KEY) || '');
    const ts = saved.get('timestamp');
    if (ts && hoursSince(ts) < 24) return saved;
    if (ts && saved.get('token')) savedLoginExpired = true;
    if ([...saved.keys()].length) clearSavedLogin();
  } catch { /* storage blocked or corrupt */ }
  return null;
};

// Login params from the 4for4 link, captured once at load. In-app navigation
// drops the query string, so a reload (pull-to-refresh, or iOS reloading a
// backgrounded tab) used to come back with none and show "Please log in";
// the 4for4 page can also serve the iframe URL with no params at all in a
// new tab. Keep a copy on the device and fall back to it while it's under
// 24h old. A URL that carries a token always wins and replaces the copy.
const LOGIN = (() => {
  const src = new URLSearchParams(window.location.search);
  const fromUrl = new URLSearchParams();
  ['name', 'timestamp', 'token', 'plan'].forEach((k) => {
    const v = src.get(k);
    if (v) fromUrl.set(k, v);
  });
  if (fromUrl.has('token')) {
    try { localStorage.setItem(LOGIN_STORE_KEY, fromUrl.toString()); } catch { /* storage blocked */ }
    return fromUrl;
  }
  if ([...fromUrl.keys()].length) return fromUrl;   // partial params: checkUserAuth rejects them
  return readSavedLogin() || fromUrl;
})();

// Forwarded to the server so it can verify the token itself instead of
// trusting the client.
const LOGIN_PARAMS = LOGIN.toString();

// The login timestamp in use (from the URL or this tab's saved copy)
export const loginTimestamp = () => LOGIN.get('timestamp');

// Append the login params to a server URL (WebSocket or REST)
export const withAuthQuery = (url) =>
  LOGIN_PARAMS ? `${url}${url.includes('?') ? '&' : '?'}${LOGIN_PARAMS}` : url;

// Plan codes that grant SharpStack access: anything containing 'sharpstack' or an '_ss_'/'_ss' token
export const hasSharpStackPlan = (plan) => {
  const p = (plan || '').trim().toLowerCase();
  return p.includes('sharpstack') || /(^|_)ss(_|$)/.test(p);
};
const validateAuthToken = ({ name, timestamp, token, secretKey = '4for4-betting-user' }) => {
  const tokenData = {
    name: name || 'anonymous',
    hour: timestamp,
  };

  let computedToken = CryptoJS.HmacSHA256(
    JSON.stringify(tokenData),
    secretKey
  ).toString(CryptoJS.enc.Base64);

  computedToken = computedToken
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');

  return computedToken === token;
};

const isDevEnvironment = () => {
  const hostname = window.location.hostname;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.includes('.dev') || // For custom dev domains
    hostname.includes('.local') // For local network testing
  );
};

// Function to get query string parameters and validate
export const checkUserAuth = () => {
  const urlParams = LOGIN;
  const name = urlParams.get('name') || 'anonymous';
  const timestamp = urlParams.get('timestamp');
  const token = urlParams.get('token');
  const plan = urlParams.get('plan');
  const isInIframe = window.self !== window.top;
  
  logger.log('Auth check params:', { name, timestamp, token, plan, isInIframe });
      if (!isInIframe && isDevEnvironment()) {
      // For development/testing when not in iframe
      return { isValid: true, name: 'SharpStackUser', reason: 'dev-mode', plan: ''};
    }
  //If no auth params provided, return as unauthenticated guest
  if (!timestamp && !token && !plan) {
    if (savedLoginExpired) {
      logger.log('Saved login expired - guest user');
      return { isValid: false, name: '', reason: 'expired' };
    }
    logger.log('No auth parameters provided - guest user');
    return { isValid: false, name: '', reason: 'no-params' };
  }

  // If partial auth params, it's an invalid auth attempt
  if (!name || !timestamp || !token || !plan) {
    logger.log('Missing some auth parameters');

    return { isValid: false, name: null, reason: 'missing-params' };
  }

  // Validate the token
  let isValid = validateAuthToken({
    name,
    timestamp,
    token,
    plan,
    secretKey: '4for4-betting-user',
  });

  //Check if user doesn't have betting plan
if (plan && !hasSharpStackPlan(plan)) {
  logger.log('User does not have betting plan:', plan);
  return { isValid: false, name: name, reason: 'no-betting-plan', plan: plan };
}

  // Additional validation rules
  if (name === 'anonymous') {
    isValid = false;
  }
  return { isValid, name: isValid ? name : null, reason: isValid ? 'success' : 'invalid-token', plan: plan };
};

const EXPIRED_RELOAD_KEY = 'sharpstackExpiredReload';

// Function to force refresh after 24 hours. When the token comes from the URL
// (a stale 4for4 iframe link), the reload brings the same token back, so
// reloading on every load looped forever. Reload at most once per timestamp
// per tab; if that can't be remembered, don't reload at all. The saved copy
// is left for the reload's readSavedLogin to find stale and remove, which is
// how that load knows to report 'expired'.
export const forceRefreshIfExpired = (timestamp) => {
  if (hoursSince(timestamp) < 24) return;
  try {
    if (sessionStorage.getItem(EXPIRED_RELOAD_KEY) === timestamp) return;
    sessionStorage.setItem(EXPIRED_RELOAD_KEY, timestamp);
  } catch { return; }   // storage blocked
  logger.log('Token expired, forcing refresh');
  window.location.reload(); // Or redirect to 4for4 login
};